<template>
  <div class="catch-star flex flex-col h-full w-full items-center justify-center">
    <div v-if="gameOver" class="text-center mb-6">
      <p class="text-2xl font-bold text-[#1c4848] mb-1">{{ gameOverWin ? 'Time\'s up!' : 'Game Over' }}</p>
      <p class="text-[#1c4848]/70">Score: {{ score }}</p>
    </div>
    <div
      ref="arenaRef"
      class="catch-star-arena relative rounded-2xl overflow-hidden bg-[#1c4848]/5 border-2 border-[#1c4848]/10"
      :style="{ width: arenaSize + 'px', height: arenaSize + 'px' }"
      @click="onArenaClick"
    >
      <div
        v-for="obj in falling"
        :key="obj.id"
        class="falling-object absolute cursor-pointer select-none"
        :class="obj.type === 'star' ? 'text-[#ecd79f]' : 'text-[#1c4848]/80'"
        :style="objectStyle(obj)"
        @click.stop="onObjectClick(obj)"
      >
        <template v-if="obj.type === 'star'">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15 9 22 9 17 14 18 21 12 18 6 21 7 14 2 9 9 9"/></svg>
        </template>
        <template v-else>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
        </template>
      </div>
    </div>
    <p class="mt-4 text-sm text-[#1c4848]/60">Tap stars to catch, avoid meteors.</p>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { parseCatchStarConfig } from './parseGameConfig.js';

const props = defineProps({ game: { type: Object, required: true } });
const emit = defineEmits(['win', 'stats', 'gameover']);

const config = computed(() => parseCatchStarConfig(props.game?.config));
const arenaRef = ref(null);
const arenaSize = 320;

const falling = ref([]);
const score = ref(0);
const lives = ref(3);
const timeLeft = ref(60);
const gameOver = ref(false);
const gameOverWin = ref(false);
const nextSpawnAt = ref(0);
let objectId = 0;
let spawnTimer = null;
let gameTimer = null;
let lastTime = 0;
let animId = null;

const objectSpeed = 120;

function objectStyle(obj) {
  return {
    left: obj.x + 'px',
    top: obj.y + 'px',
    width: obj.type === 'star' ? '32px' : '28px',
    height: obj.type === 'star' ? '32px' : '28px',
    marginLeft: obj.type === 'star' ? '-16px' : '-14px',
    marginTop: obj.type === 'star' ? '-16px' : '-14px',
  };
}

function getRandomX() {
  return 40 + Math.random() * (arenaSize - 80);
}

function spawnObject() {
  const isStar = Math.random() < 0.65;
  falling.value = [...falling.value, {
    id: ++objectId,
    type: isStar ? 'star' : 'meteor',
    x: getRandomX(),
    y: -30,
  }];
  const base = config.value.spawnInterval * 1000;
  const range = config.value.spawnIntervalRange * 1000;
  nextSpawnAt.value = Date.now() + base + (Math.random() * 2 - 1) * range;
}

function onArenaClick(e) {
  if (gameOver.value) return;
  const rect = arenaRef.value?.getBoundingClientRect();
  if (!rect) return;
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  for (const obj of falling.value) {
    const cx = obj.x;
    const cy = obj.y;
    const r = 24;
    if ((x - cx) ** 2 + (y - cy) ** 2 <= r * r) {
      onObjectClick(obj);
      return;
    }
  }
}

function onObjectClick(obj) {
  if (gameOver.value) return;
  if (obj.type === 'star') {
    score.value += 10;
    falling.value = falling.value.filter((o) => o.id !== obj.id);
  } else {
    lives.value -= 1;
    falling.value = falling.value.filter((o) => o.id !== obj.id);
    if (lives.value <= 0) endGame(false);
  }
  emit('stats', { score: score.value, lives: lives.value, timeLeft: timeLeft.value });
}

function endGame(won) {
  gameOver.value = true;
  gameOverWin.value = won;
  if (spawnTimer) clearTimeout(spawnTimer);
  if (gameTimer) clearInterval(gameTimer);
  if (animId) cancelAnimationFrame(animId);
  emit('gameover', { score: score.value, won });
  if (won) emit('win');
}

function tick(now) {
  if (gameOver.value) return;
  const dt = lastTime ? (now - lastTime) / 1000 : 0;
  lastTime = now;
  const move = objectSpeed * dt;
  falling.value = falling.value.map((o) => ({ ...o, y: o.y + move })).filter((o) => {
    if (o.y > arenaSize + 20) {
      if (o.type === 'meteor') {
        lives.value -= 1;
        if (lives.value <= 0) endGame(false);
      }
      return false;
    }
    return true;
  });
  if (Date.now() >= nextSpawnAt.value) spawnObject();
  emit('stats', { score: score.value, lives: lives.value, timeLeft: timeLeft.value });
  animId = requestAnimationFrame(tick);
}

function start() {
  falling.value = [];
  score.value = 0;
  lives.value = config.value.lives;
  timeLeft.value = Math.floor(config.value.timeLimit);
  gameOver.value = false;
  gameOverWin.value = false;
  nextSpawnAt.value = Date.now() + 800;
  lastTime = 0;
  emit('stats', { score: 0, lives: lives.value, timeLeft: timeLeft.value });
  gameTimer = setInterval(() => {
    if (gameOver.value) return;
    timeLeft.value -= 1;
    if (timeLeft.value <= 0) endGame(true);
    else emit('stats', { score: score.value, lives: lives.value, timeLeft: timeLeft.value });
  }, 1000);
  animId = requestAnimationFrame(tick);
}

watch(() => [props.game?.id, config.value.lives, config.value.timeLimit], () => {
  if (props.game?.id) start();
}, { immediate: true });

onBeforeUnmount(() => {
  if (spawnTimer) clearTimeout(spawnTimer);
  if (gameTimer) clearInterval(gameTimer);
  if (animId) cancelAnimationFrame(animId);
});

defineExpose({ restart: start });
</script>

<style scoped>
.catch-star-arena { touch-action: none; }
.falling-object { pointer-events: none; }
.catch-star-arena .falling-object { pointer-events: auto; }
</style>
